import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import { Shield, FileText, Users, Database, Lock, Clock, UserCheck, Cookie, Baby, Globe, RefreshCw, Mail, Scale, CheckCircle2 } from 'lucide-react';

export function PrivacyPage() {
  const { t } = useTranslation();
  const location = useLocation();

  // Scroll to hash on load
  useEffect(() => {
    if (location.hash) {
      const timeoutId = setTimeout(() => {
        const element = document.getElementById(location.hash.slice(1));
        if (element) {
          const headerOffset = 100;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [location.hash]);

  const sections = [
    { id: 'collection', icon: Database },
    { id: 'usage', icon: FileText },
    { id: 'sharing', icon: Users },
    { id: 'security', icon: Lock },
    { id: 'retention', icon: Clock },
    { id: 'rights', icon: UserCheck },
    { id: 'cookies', icon: Cookie },
    { id: 'children', icon: Baby },
    { id: 'international', icon: Globe },
    { id: 'changes', icon: RefreshCw },
    { id: 'contact', icon: Mail },
  ];

  const kvkkRights = t('kvkk.rights.items', { returnObjects: true }) as string[];

  return (
    <>
      <SEO {...SEO_CONFIG.privacy} />
      <div className="min-h-screen">
        {/* Hero */}
        <section className="section-lg relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent" />

          <div className="container relative">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm mb-6">
                <Shield size={16} />
                <span>{t('privacy.lastUpdated')}: 28 Aralik 2025</span>
            </div>

            <h1 className="heading-xl mb-6">
              {t('privacy.title')}{' '}
              <span className="text-accent">{t('privacy.titleHighlight')}</span>
            </h1>
            <p className="text-lg text-text-secondary">
              {t('privacy.intro')}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="prose prose-invert max-w-none">
              {sections.map((section) => (
                <div key={section.id} id={section.id} className="mb-8 scroll-mt-28">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                      <section.icon className="text-accent" size={20} />
                    </div>
                    <h2 className="text-xl font-semibold text-text-primary">
                      {t(`privacy.sections.${section.id}.title`)}
                    </h2>
                  </div>
                  <p className="text-text-secondary leading-relaxed pl-13">
                    {t(`privacy.sections.${section.id}.content`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* KVKK Section */}
      <section id="kvkk" className="section bg-bg-surface scroll-mt-28">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm mb-6">
                <Scale size={16} />
                <span>{t('kvkk.lawNumber')}</span>
              </div>
              <h2 className="heading-lg mb-4">
                {t('kvkk.title')}
              </h2>
              <p className="text-text-secondary">
                {t('kvkk.description')}
              </p>
            </div>

            {/* Veri Sorumlusu */}
            <div className="card p-6 mb-8 border-blue-500/20 bg-gradient-to-br from-blue-500/5 to-transparent">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Shield className="text-blue-400" size={20} />
                {t('kvkk.dataController.title')}
              </h3>
              <p className="text-text-secondary text-sm">
                <strong>{t('kvkk.dataController.company')}</strong><br />
                {t('kvkk.dataController.address')}<br />
                {t('kvkk.dataController.email')}
              </p>
            </div>

            {/* Haklar */}
            <div className="mb-8">
              <h3 className="font-semibold mb-4">{t('kvkk.rights.title')}</h3>
              <div className="space-y-3">
                {kvkkRights.map((right, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 rounded-lg bg-bg-card border border-border-default">
                    <CheckCircle2 className="text-green-500 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-text-secondary text-sm">{right}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Basvuru */}
            <div className="card p-6 border-accent/20">
              <h3 className="font-semibold mb-3">{t('kvkk.application.title')}</h3>
              <p className="text-text-secondary text-sm mb-4">
                {t('kvkk.application.description')}
              </p>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                  {t('kvkk.application.email')} <a href="mailto:kvkk@bosphoruslabs.com" className="text-accent hover:underline">kvkk@bosphoruslabs.com</a>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                  {t('kvkk.application.mail')}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="section">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-md mb-6">{t('cookiesPage.relatedPolicies.title')}</h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/cookies" className="btn btn-secondary">
                {t('cookiesPage.pageTitle')}
              </Link>
              <Link to="/security" className="btn btn-secondary">
                {t('footer.security')}
              </Link>
              <Link to="/terms" className="btn btn-secondary">
                {t('footer.terms')}
              </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
